import { includes, keyBy, map, maxBy, orderBy } from "lodash";

import { ACTIVE_OFFER_STATES } from "services/offers/constants";

import { ListingOffer } from "./types";

interface OfferChain {
  offerChain: ListingOffer[];
  latestOfferCreatedAt: string;
  hasActiveOffer: boolean;
  highestBuyerAmount: number;
}

interface OffersByOfferId {
  [key: string]: ListingOffer;
}

export const buildSortedOfferChains = (offers: ListingOffer[]) => {
  // get all offers that are not counteroffers (i.e. they are the first offer in a chain)
  const initialOffers = offers.filter(offer => !offer.countered_offer_id);
  const offersByOfferId: OffersByOfferId = keyBy(offers, "id");

  const offerChains = map(initialOffers, initialOffer => {
    const offerChain = buildChain(initialOffer, offersByOfferId);
    const latestOfferCreatedAt = offerChain[0].created_at;
    const hasActiveOffer = isActiveChain(offerChain);
    const highestBuyerAmount = highestAmountInChain(offerChain);

    return {
      offerChain,
      latestOfferCreatedAt,
      hasActiveOffer,
      highestBuyerAmount,
    };
  });
  return sortOfferChains(offerChains);
};

const buildChain = (
  initialOffer: ListingOffer,
  offersByOfferId: OffersByOfferId,
) => {
  let chain = [initialOffer];
  let counteroffer = offersByOfferId[initialOffer.counteroffer_id];

  while (counteroffer) {
    chain = [counteroffer, ...chain];
    counteroffer = offersByOfferId[counteroffer.counteroffer_id];
  }
  return chain;
};

const isActiveChain = (chain: ListingOffer[]) => {
  return chain.some(offer => includes(ACTIVE_OFFER_STATES, offer.state));
};

const highestAmountInChain = (chain: ListingOffer[]) => {
  const maxOffer = maxBy(chain, offer =>
    offer.kind === "buyer_counteroffer" || offer.kind === "bid_offer"
      ? parseFloat(offer.amount)
      : 0,
  );

  return maxOffer ? parseFloat(maxOffer.amount) : 0;
};

const sortOfferChains = (offerChains: OfferChain[]) => {
  const activeOffers = orderBy(
    offerChains.filter(offerChain => offerChain.hasActiveOffer),
    ["highestBuyerAmount"],
    ["desc"],
  );

  const inactiveOffers = orderBy(
    offerChains.filter(offerChain => !offerChain.hasActiveOffer),
    ["latestOfferCreatedAt"],
    ["desc"],
  );
  return [...activeOffers, ...inactiveOffers];
};
